import React, { useEffect, useRef, useState } from 'react';

import { Box, Container, Grid, GridSize, Paper, Theme } from '@mui/material';
import { Breakpoint } from '@mui/material/styles';

import DefaultBackgroundImage from '@images/home/hero/tableau1x.jpg';
import { clamp, lerp } from '@frontend/utils/math';
import { AppearOnScroll } from '@frontend/components/common/AppearOnScroll';
import styles from '@frontend/components/unauthenticated/pages/Pages.scss';

type HeroFormPropsType = {
  children: any;
  imageUrl?: string;
} & Partial<Record<Breakpoint, boolean | GridSize>>;

function HeroForm({ children, imageUrl, xs, sm, md, lg, xl }: HeroFormPropsType) {
  // follow mouse effect
  const box = useRef<HTMLDivElement>(null);
  const [backgroundPosition, setBackgroundPosition] = useState('50% 50%');
  const onMouseMove = (e) => {
    if (!box.current) return;
    onMove(e.clientX, e.clientY, box.current.getBoundingClientRect());
  };
  const onTouchMove = (e) => {
    if (!box.current) return;
    onMove(e.touches[0].clientX, e.touches[0].clientY, box.current.getBoundingClientRect());
  };
  const onMove = (clientX, clientY, rect) => {
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const bx = lerp(45, 55, x / rect.width);
    const by = lerp(45, 55, y / rect.height);
    setBackgroundPosition(`${bx}% ${by}%`);
  };
  const windowScroll = (e) => {
    if (!box.current) return;
    const rect = box.current.getBoundingClientRect();
    const value = rect.y / rect.height;
    const yPercent = -1 * clamp(value * 3, -1, 0);
    setBackgroundPosition((backgroundPosition) => {
      const [bx, by] = backgroundPosition.split(' ');
      const by2 = parseFloat(by.replace('%', ''));
      const nextBy = lerp(45, 65, yPercent);
      return `${bx} ${nextBy}%`;
    });
  };
  useEffect(() => {
    window.addEventListener('scroll', windowScroll);
    return () => window.removeEventListener('scroll', windowScroll);
  }, []);
  // end of follow mouse effect

  return (
    <Box
      sx={{
        backgroundImage: `url(${imageUrl || DefaultBackgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: backgroundPosition,
        transition: 'background-position 0.5s ease-out',
      }}
      display="flex"
      minHeight="100vh"
      ref={box}
      onTouchMove={onTouchMove}
      onMouseMove={onMouseMove}
    >
      <AppearOnScroll percent={0.1} sx={{ width: '100%', margin: 'auto' }}>
        <Container maxWidth="lg">
          <Grid
            container
            sx={{
              height: '100%',
              paddingTop: (theme: Theme) => {
                return {
                  xs: `${theme.menu.mobile.height}`,
                  sm: `${theme.menu.desktop.height}`,
                };
              },
            }}
            justifyContent="center"
            alignItems="center"
          >
            <Grid item xs={xs} sm={sm} md={md} lg={lg} xl={xl} className={styles.slideInOnAppear}>
              <Box display="flex" justifyContent="center" alignItems="center" height="100%" my={2}>
                <Paper square={true} sx={{ width: '100%' }}>
                  <Box py={2}>{children}</Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </AppearOnScroll>
    </Box>
  );
}

export default HeroForm;
